import React from 'react';
import { 
  Restaurant, 
  LocalHospital, 
  Brush, 
  Park 
} from '@mui/icons-material';
import facilitiesModernClassrooms from '../images/facilities/facilitiesModernClassrooms.jpg';
import facilitiesComputerLabs from '../images/facilities/facilitiesComputerLabs.jpg';
import facilitiesSportsRecreation from '../images/facilities/facilitiesSportsRecreation.jpg';

const Facilities = () => {
  return (
    <section id="facilities" className="py-16 bg-white">
      <div className="max-w-7xl mx-auto px-4">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-blue-900 mb-4">World-Class Facilities</h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">State-of-the-art facilities designed to enhance learning and provide students with the best educational experience</p>
        </div>

        <div className="flex flex-col gap-16">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-12">
            <div className="bg-gray-50 rounded-xl overflow-hidden shadow-md transition-transform hover:-translate-y-1 hover:shadow-lg">
              <div className="h-52 flex items-center justify-center text-white text-xl font-medium" style={{backgroundImage: `url(${facilitiesModernClassrooms})`, backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: 'no-repeat', boxShadow: 'inset 0 0 0 1000px rgba(0,0,64,0.4)'}}>
                Modern Classrooms
              </div>
              <div className="p-8">
                <h3 className="text-blue-800 text-xl font-semibold mb-4">Modern Classrooms</h3>
                <p className="text-gray-600 leading-relaxed mb-4">Spacious, well-lit classrooms equipped with interactive whiteboards, comfortable seating, and the latest educational technology to create an optimal learning environment.</p>
                <ul className="list-none p-0 m-0 space-y-2">
                  <li className="relative pl-6 text-gray-600 before:content-['✓'] before:absolute before:left-0 before:text-green-500 before:font-bold">Interactive whiteboards and projectors</li>
                  <li className="relative pl-6 text-gray-600 before:content-['✓'] before:absolute before:left-0 before:text-green-500 before:font-bold">Ergonomic furniture and seating</li>
                  <li className="relative pl-6 text-gray-600 before:content-['✓'] before:absolute before:left-0 before:text-green-500 before:font-bold">Climate-controlled environment</li>
                  <li className="relative pl-6 text-gray-600 before:content-['✓'] before:absolute before:left-0 before:text-green-500 before:font-bold">Natural lighting and ventilation</li>
                </ul>
              </div>
            </div>
            <div className="bg-gray-50 rounded-xl overflow-hidden shadow-md transition-transform hover:-translate-y-1 hover:shadow-lg">
              <div className="h-52 flex items-center justify-center text-white text-xl font-medium" style={{backgroundImage: `url(${facilitiesComputerLabs})`, backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: 'no-repeat', boxShadow: 'inset 0 0 0 1000px rgba(0,0,64,0.4)'}}>
                Science & Computer Labs
              </div>
              <div className="p-8">
                <h3 className="text-blue-800 text-xl font-semibold mb-4">Science & Computer Labs</h3>
                <p className="text-gray-600 leading-relaxed mb-4">Fully equipped labs for hands-on experiments and technology learning, fostering curiosity and innovation.</p>
                <ul className="list-none p-0 m-0 space-y-2">
                  <li className="relative pl-6 text-gray-600 before:content-['✓'] before:absolute before:left-0 before:text-green-500 before:font-bold">Modern science equipment</li>
                  <li className="relative pl-6 text-gray-600 before:content-['✓'] before:absolute before:left-0 before:text-green-500 before:font-bold">Computer labs with high-speed internet</li>
                  <li className="relative pl-6 text-gray-600 before:content-['✓'] before:absolute before:left-0 before:text-green-500 before:font-bold">STEM project spaces</li>
                </ul>
              </div>
            </div>
            <div className="bg-gray-50 rounded-xl overflow-hidden shadow-md transition-transform hover:-translate-y-1 hover:shadow-lg">
              <div className="h-52 flex items-center justify-center text-white text-xl font-medium" style={{backgroundImage: `url(${facilitiesSportsRecreation})`, backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: 'no-repeat', boxShadow: 'inset 0 0 0 1000px rgba(0,0,64,0.4)'}}>
                Sports & Recreation
              </div>
              <div className="p-8">
                <h3 className="text-blue-800 text-xl font-semibold mb-4">Sports & Recreation</h3>
                <p className="text-gray-600 leading-relaxed mb-4">Extensive sports facilities including fields, courts, and a swimming pool to promote physical fitness and teamwork.</p>
                <ul className="list-none p-0 m-0 space-y-2">
                  <li className="relative pl-6 text-gray-600 before:content-['✓'] before:absolute before:left-0 before:text-green-500 before:font-bold">Multipurpose sports fields</li>
                  <li className="relative pl-6 text-gray-600 before:content-['✓'] before:absolute before:left-0 before:text-green-500 before:font-bold">Indoor gymnasium</li>
                  <li className="relative pl-6 text-gray-600 before:content-['✓'] before:absolute before:left-0 before:text-green-500 before:font-bold">Swimming pool</li>
                  <li className="relative pl-6 text-gray-600 before:content-['✓'] before:absolute before:left-0 before:text-green-500 before:font-bold">Playgrounds and parks</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="mt-12 text-center">
            <h3 className="text-blue-800 text-2xl font-semibold mb-6">Facilities Highlights</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-8">
              <div className="flex flex-col items-center bg-white rounded-xl p-8 shadow-md">
                <div className="text-3xl text-blue-700 mb-4"><Restaurant fontSize="inherit" /></div>
                <h4 className="text-blue-900 font-semibold mb-2">Cafeteria</h4>
                <p className="text-gray-600 text-center text-sm">Nutritious meals and snacks in a clean, welcoming environment.</p>
              </div>
              <div className="flex flex-col items-center bg-white rounded-xl p-8 shadow-md">
                <div className="text-3xl text-blue-700 mb-4"><LocalHospital fontSize="inherit" /></div>
                <h4 className="text-blue-900 font-semibold mb-2">Health Center</h4>
                <p className="text-gray-600 text-center text-sm">On-site medical care and wellness support for students.</p>
              </div>
              <div className="flex flex-col items-center bg-white rounded-xl p-8 shadow-md">
                <div className="text-3xl text-blue-700 mb-4"><Brush fontSize="inherit" /></div>
                <h4 className="text-blue-900 font-semibold mb-2">Art Studio</h4>
                <p className="text-gray-600 text-center text-sm">Creative spaces for visual arts, crafts, and design projects.</p>
              </div>
              <div className="flex flex-col items-center bg-white rounded-xl p-8 shadow-md">
                <div className="text-3xl text-blue-700 mb-4"><Park fontSize="inherit" /></div>
                <h4 className="text-blue-900 font-semibold mb-2">Green Spaces</h4>
                <p className="text-gray-600 text-center text-sm">Beautiful gardens and outdoor areas for relaxation and learning.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Facilities; 